/**
 * columnDefs.js — DebtLedgerTable column definitions (Muhasebe FAZ 3.6, PLAN §11.3).
 *
 * `DataTable` (components/admin/DataTable.jsx) has NO built-in column-visibility
 * or responsive-hide feature (18 existing consumers, extending it was judged
 * riskier than solving this at the wrapper level — see DebtLedgerTable.jsx).
 * Each column definition here carries a `tier` tag that the wrapper uses to
 * decide which columns to actually pass into `<DataTable columns={...} />`:
 *
 *   'required' — always visible (Eğitim, Borç Tarihi, Borç Tutarı, Kalan, Durum, Aksiyon)
 *   'default'  — visible only at >=1280px (Ödeme Tarihi, Ödeme Tutarı, Senet No, Kaydeden)
 *   'optional' — behind "Sütun Seç" (KDV Tutarı, KDV'siz Tutarı)
 *
 * NOTE on the plan's third "Sütun Seç" column, "Ödeme Tutarı (Aktarılan)" /
 * "Hesaba Geçen Tutar" (net amount after bank/POS commission, PLAN §11.4):
 * the ledger endpoint (`GET /admin/students/{id}/accounting/ledger`) does NOT
 * return a post-commission net-settled field today — only `vat_amount_row`
 * and `net_of_vat_row` (both VAT-derived, services/payment_vat.py). Rather
 * than fabricate a value, this column is NOT implemented this turn (flagged
 * to the coordinator as an open item — needs a backend field first).
 *
 * `net_of_vat_row` (amount minus VAT) is exactly "KDV'siz Tutarı" — mapped
 * there, not to the (unimplemented) net-settled column.
 *
 * FAZ 1-B (Seçenek C, faiz, 2026-08-24) — `collection_rows[].interest_amount_row`
 * (yalnızca `kind === 'installment'` satırlarında dolu) `interest_amount`
 * kolonuna eşlenir, `tier: 'optional'` ("Sütun Seç" arkasında; faiz motoru
 * kapalıyken çoğu satırda 0/boş olacağı için varsayılan görünümde gürültü
 * yapmasın diye). `summary.toplam_faiz`'in AKSİNE bu satır-bazlı bir alan —
 * kalan_bakiye hesabına KATILMAZ (bkz. DebtLedgerSubTab.jsx aynı disiplin).
 */

import { useState } from 'react';
import { CreditCard, Info, AlertTriangle, Scale, Scissors, Merge, Hourglass, FileText, Clock, Undo2, Link2 } from 'lucide-react';
import { fmtMoneyAccounting } from '@/utils/money';
import StatusBadge from '@/components/admin/StatusBadge';
import ActionMenu from '@/components/admin/ActionMenu';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/contexts/ToastContext';
import { useDownloadInvoicePdf } from '@/api/mutations/accountingDocuments';
import { ActionButton } from './noteUiKit';
import { ParasutStatusCell } from './parasutStatusCell';

// ── Durum sözlüğü (PLAN §11.6) ────────────────────────────────────────────────
// debt_rows.status kaynağı invoice.status (issued/partially_paid/paid/cancelled)
// VEYA installment.status (pending/paid/overdue) olabilir — ikisi de aynı
// görsel sözlüğe eşlenir. `interest`/`legal`/`overdue_warn`/`split`/`merged`
// bu turda backend'den hiç dönmüyor (FAZ 4/5 konusu) ama sözlük PLAN'a göre
// eksiksiz tanımlanır — ileride backend bu değerleri döndürmeye başladığında
// frontend tarafında değişiklik gerekmez.
export const STATUS_CONFIG = {
  pending: { variant: 'warning', key: 'accounting.status.pending' },
  issued: { variant: 'warning', key: 'accounting.status.pending' },
  due: { variant: 'info', key: 'accounting.status.due' },
  partially_paid: { variant: 'info', key: 'accounting.status.due' },
  overdue: { variant: 'danger', key: 'accounting.status.overdue' },
  overdue_warn: { variant: 'danger', key: 'accounting.status.overdue_warn', icon: AlertTriangle },
  interest: { variant: 'purple', key: 'accounting.status.interest' },
  legal: { variant: 'purple', key: 'accounting.status.legal', icon: Scale },
  paid: { variant: 'success', key: 'accounting.status.paid' },
  completed: { variant: 'success', key: 'accounting.status.paid' },
  cancelled: { variant: 'neutral', key: 'accounting.status.cancelled' },
  // FAZ 4.8 — PLAN §866-867: split -> "Parçalandı"/Scissors, merged ->
  // "Birleştirildi"/Merge, ikisi de neutral varyant.
  split: { variant: 'neutral', key: 'accounting.status.split', icon: Scissors },
  merged: { variant: 'neutral', key: 'accounting.status.merged', icon: Merge },
};

// B2 fix (kriter 3) — tahsil edilemez (terminal) borç satırı statüleri.
// Backend SSOT `backend/services/installment_status.py::
// INSTALLMENT_TERMINAL_STATUSES` ile birebir örtüşür (paid, cancelled).
// Tek kaynak burada — DebtLedgerSubTab.jsx (openCollection guard'ı, form
// açılmasını engeller) ve DebtLedgerTable.jsx (`MobileLedgerCards`, buton
// görselini kapatır) İKİSİ DE bu listeyi import eder; kopya liste türetilmez.
export const TERMINAL_DEBT_ROW_STATUSES = ['paid', 'cancelled'];

const STRIP_COLORS = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  info: 'bg-sky-500',
  neutral: 'bg-gray-400',
  purple: 'bg-purple-500',
};

export function statusBadgeProps(status, t) {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return { variant: 'neutral', label: status || '—', icon: null };
  return { variant: cfg.variant, label: t(cfg.key), icon: cfg.icon || null };
}

// ── Tahsilat türü sözlüğü (collection_rows[].kind, backend'de kanonik olarak
// PLAN'da tanımlı — bkz. MUHASEBE_VERI_MODELI.md §1.1: sale / down_payment /
// installment / promissory / self_purchase / refund / manual). Bu turda
// `routes/payments.py:admin_student_accounting_ledger` içinde GERÇEKTEN
// üretilen değerler: sale (sales_service.py:639, havale_service.py:192),
// down_payment (sales_service.py:671, routes/admin.py add-payment), installment
// (routes/payments.py:1256), promissory (routes/payments.py:1804), refund
// (services/refund_service.py:360, routes/payments.py:306), manual
// (routes/admin.py add-payment, kind Literal default). `self_purchase`
// dokümante edilmiş ama backend'de HİÇBİR yerde yazılmıyor (grep: 0 sonuç) —
// bilinçli olarak sözlüğe eklenmedi; aşağıdaki fallback (statusBadgeProps'la
// AYNI desen) bu ve gelecekte eklenebilecek herhangi bir yeni kind değerini
// çökmeden, ham değeri nötr rozette göstererek karşılar.
export const KIND_CONFIG = {
  sale: { variant: 'success', key: 'accounting.kind.sale' },
  down_payment: { variant: 'primary', key: 'accounting.kind.down_payment' },
  installment: { variant: 'info', key: 'accounting.kind.installment' },
  promissory: { variant: 'purple', key: 'accounting.kind.promissory' },
  refund: { variant: 'danger', key: 'accounting.kind.refund' },
  manual: { variant: 'neutral', key: 'accounting.kind.manual' },
  // FAZ 6.8 — onaylanmış ters kayıt satırları backend'de kind: "reversal" ile
  // üretilir (bkz. services/reversal_service.py); refund'un (danger/kırmızı,
  // "para dışarı çıktı") aksine bu bir DÜZELTME kaydıdır, o yüzden refund ile
  // aynı danger tonu yerine nötr/uyarı tonunda `warning` (amber) seçildi —
  // renk yalnızca fark yaratıcı, anlamı zaten label taşıyor (kind rozetleri
  // hiçbir zaman icon göstermiyor, bkz. kindBadgeProps altı ve
  // DebtLedgerTable.jsx'teki <StatusBadge> çağrısı — mevcut 6 girdiyle aynı
  // desen korundu).
  reversal: { variant: 'warning', key: 'accounting.kind.reversal' },
};

export function kindBadgeProps(kind, t) {
  const cfg = KIND_CONFIG[kind];
  if (!cfg) return { variant: 'neutral', label: kind || '—' };
  return { variant: cfg.variant, label: t(cfg.key) };
}

// ── Senet (promissory_notes.status) durum sözlüğü ─────────────────────────────
// Bu domain debt_rows.status'tan AYRI: promissory_notes kendi status alanını
// taşır. FAZ 4 ile birlikte backend'de GERÇEKTEN üretilen 9 değerin tamamı:
//   active      — oluşturma (routes/payments.py, services/sales_service.py)
//   collecting  — ÇAPA: tahsilat sürüyor (anchor_note_for_collection, CAS ile
//                 `active -> collecting`; başarıda `collected`, para hiç
//                 yazılmadan hata alınırsa `active`'e geri bırakılır)
//   collected   — tahsilat (PUT .../{note_id}, INV-2: yalnız active'den)
//   cancelled   — POST .../{note_id}/cancel (gerekçe zorunlu)
//   split       — POST .../{note_id}/split sonrası EBEVEYN senedin durumu
//   merged      — POST .../merge sonrası KAYNAK senetlerin durumu
//   legal       — POST .../legal-transfer (icra dosyasına devir)
//   splitting   — ÇAPA: parçalama yarıda kaldı (taksit kapatılamadı)
//   merging     — ÇAPA: birleştirme yarıda kaldı
//
// `collecting`/`splitting`/`merging` NORMAL bir durak DEĞİL: işlem yarıda
// kalmış demektir, muhasebe mutabakatı ister. Bu yüzden `danger` varyant +
// Hourglass ikonu ile diğerlerinden görsel olarak ayrışır ve ekranda TÜM
// aksiyonlar kapatılır (bkz. promissoryMath.js::noteActionAvailability).
export const PROMISSORY_STATUS_CONFIG = {
  active: { variant: 'info', key: 'accounting.promissory_notes.status_active' },
  collecting: { variant: 'danger', key: 'accounting.status.collecting', icon: Hourglass },
  collected: { variant: 'success', key: 'accounting.status.collected' },
  cancelled: { variant: 'neutral', key: 'accounting.status.cancelled' },
  split: { variant: 'neutral', key: 'accounting.status.split', icon: Scissors },
  merged: { variant: 'neutral', key: 'accounting.status.merged', icon: Merge },
  legal: { variant: 'purple', key: 'accounting.status.legal', icon: Scale },
  splitting: { variant: 'danger', key: 'accounting.status.splitting', icon: Hourglass },
  merging: { variant: 'danger', key: 'accounting.status.merging', icon: Hourglass },
};

export function promissoryStatusBadgeProps(status, t) {
  const cfg = PROMISSORY_STATUS_CONFIG[status];
  if (!cfg) return { variant: 'neutral', label: status || '—', icon: null };
  return { variant: cfg.variant, label: t(cfg.key), icon: cfg.icon || null };
}

export function fmtLedgerDate(d, lang) {
  if (!d) return '—';
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US');
}

const Dash = () => <span className="text-[var(--text-muted)]">—</span>;

/**
 * Satır-içi "Satış Kaydı PDF İndir" butonu (Muhasebe FAZ 6 "Belgeler"; etiket
 * düzeltmesi 2026-09-29, feature/parasut-fatura-faz4-web — resmi fatura DEĞİL,
 * iç satış kaydı, bkz. `accounting.docs.invoice_pdf.*` LanguageContext.js).
 *
 * `row.invoice_id` doluysa `action` kolonunda "Tahsilat" butonunun YANINDA
 * render edilir (bkz. buildColumnDefs -> 'action'). Kendi local `loading`
 * state'ini taşır — çift tıklama koruması (AdminInvoices.jsx:69,96 deseni),
 * `useDownloadInvoicePdf`'in `mutation.isPending`'i AYRICA kullanılmaz.
 */
export function InvoicePdfButton({ row, className = '' }) {
  const { t } = useLanguage();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const downloadInvoice = useDownloadInvoicePdf();

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await downloadInvoice.mutateAsync(row.invoice_id);
      toast.success(t('accounting.docs.invoice_pdf.success'));
    } catch {
      toast.error(t('accounting.docs.invoice_pdf.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ActionButton
      variant="neutral"
      onClick={handleClick}
      loading={loading}
      icon={FileText}
      testId={`invoice-pdf-btn-${row.key}`}
      aria-label={t('accounting.docs.invoice_pdf.aria_label').replace('{course}', row.course_title || row.course_id || '—')}
      className={className}
    >
      {loading ? t('accounting.docs.invoice_pdf.loading') : t('accounting.docs.invoice_pdf.label')}
    </ActionButton>
  );
}

/**
 * Satır-içi "Ödemeyi Geri Al" tetikleyicisi (Muhasebe FAZ 6.8).
 *
 * `refund` kind'i için bu bileşen HİÇ render edilmez (iade satırının kendisi
 * geri alınmaz, bkz. `reversePaymentActionCell`). `promissory` (doğrudan senet
 * tahsilatı) İÇİN ARTIK GÖSTERİLİR — backend `senetli_tahsilat_geri_alinamaz`
 * guard'ını kaldırdı (KARAR DEĞİŞİKLİĞİ 2, 2026-09-28, bkz.
 * `payment_reversal_service.py::_assert_not_note_linked` docstring'i);
 * `ReversePaymentDialog` bu satırlarda ek bir senet uyarısı gösterir. Tıklama,
 * gerçek isteği atmaz — sadece `onReverse(row)` ile dialog'u açar
 * (CollectionForm/openCollection ile AYNI desen, bkz. DebtLedgerSubTab.jsx).
 */
export function ReversePaymentButton({ row, onReverse, className = '' }) {
  const { t } = useLanguage();
  return (
    <ActionButton
      variant="neutral"
      onClick={() => onReverse(row)}
      icon={Undo2}
      testId={`reverse-payment-btn-${row.key}`}
      className={className}
    >
      {t('accounting.reverse_payment.confirm_button')}
    </ActionButton>
  );
}

/**
 * Ödeme satırının "Ödemeyi Geri Al" hücresini kararlaştırır — hem masaüstü
 * `action` kolonunda hem `MobileLedgerCards`'ta (DebtLedgerTable.jsx) AYNI
 * kararla kullanılır (kopyalama yerine tek kaynak):
 *   - `reversal_status === 'pending_approval'` -> rozet (buton YOK)
 *   - `reversal_status === 'reversed'`         -> nötr rozet (buton YOK)
 *   - `kind === 'refund'`                      -> hiçbir şey (iade satırı geri alınmaz)
 *   - aksi halde (promissory DAHİL)             -> tetikleyici buton
 * `null` dönerse çağıran kendi boş-durum gösterimini (Dash/hiçbir şey) kullanır.
 */
export function reversePaymentActionCell({ row, t, onReverse }) {
  if (row.reversal_status === 'pending_approval') {
    return <StatusBadge variant="warning" icon={Clock} label={t('accounting.reverse_payment.pending_badge')} dot />;
  }
  if (row.reversal_status === 'reversed') {
    return <StatusBadge variant="neutral" icon={Undo2} label={t('accounting.reverse_payment.reversed_label')} />;
  }
  if (row.kind === 'refund') return null;
  return <ReversePaymentButton row={row} onReverse={onReverse} />;
}

/**
 * @param {{ t: Function, lang: string, onCollect: (row) => void, onReverse: (row) => void,
 *   onMokaLink?: (row) => void, mokaConfig?: { link_active?: boolean },
 *   parasutStatusMap?: Record<string, object> }} params
 * @returns {Array} column defs, each tagged with `tier`
 */
export function buildColumnDefs({ t, lang, onCollect, onReverse, onMokaLink, mokaConfig, parasutStatusMap }) {
  return [
    // ── Zorunlu (her breakpoint) ────────────────────────────────────────────
    {
      key: 'course',
      tier: 'required',
      label: t('accounting.columns.course'),
      cellClassName: 'max-w-[220px]',
      render: (_, row) => (
        <span className="font-semibold truncate block" title={row.course_title || row.course_id || ''}>
          {row.course_title || row.course_id || <Dash />}
        </span>
      ),
    },
    {
      key: 'debt_date',
      tier: 'required',
      label: t('accounting.columns.debt_date'),
      render: (_, row) => (row.row_type !== 'payment' ? fmtLedgerDate(row.date, lang) : <Dash />),
    },
    {
      key: 'debt_amount',
      tier: 'required',
      label: t('accounting.columns.debt_amount'),
      className: 'text-right',
      cellClassName: 'text-right tabular-nums font-bold',
      render: (_, row) =>
        fmtMoneyAccounting(row.row_type !== 'payment' ? row.amount : 0, lang),
    },
    {
      key: 'remaining',
      tier: 'required',
      label: t('accounting.columns.remaining'),
      className: 'text-right',
      cellClassName: 'text-right tabular-nums font-bold',
      render: (_, row) =>
        fmtMoneyAccounting(row.row_type !== 'payment' ? row.remaining : 0, lang),
    },
    {
      key: 'status',
      tier: 'required',
      label: t('accounting.columns.status'),
      render: (_, row) => {
        if (row.row_type === 'payment') return <Dash />;
        const { variant, label, icon } = statusBadgeProps(row.status, t);
        return (
          <div className="flex items-center gap-2">
            <span
              className={cn('inline-block w-[3px] h-4 rounded-full flex-shrink-0', STRIP_COLORS[variant] || STRIP_COLORS.neutral)}
              aria-hidden
            />
            <StatusBadge variant={variant} label={label} icon={icon} dot />
          </div>
        );
      },
    },
    {
      // Peşinat (down_payment) manuel tahsilattan (manual) burada ayrışır —
      // 'required' (her breakpoint'te görünür), çünkü bu ayrım FAZ 3'ün ana
      // teslimatı: kullanıcı peşinat girip sonucu bu kolonda doğrular.
      key: 'kind',
      tier: 'required',
      label: t('accounting.columns.kind'),
      render: (_, row) => {
        if (row.row_type !== 'payment') return <Dash />;
        const { variant, label } = kindBadgeProps(row.kind, t);
        return <StatusBadge variant={variant} label={label} />;
      },
    },
    {
      key: 'action',
      tier: 'required',
      label: t('accounting.columns.action'),
      render: (_, row) => {
        if (row.row_type === 'payment') {
          return reversePaymentActionCell({ row, t, onReverse }) || <Dash />;
        }
        const isTerminal = TERMINAL_DEBT_ROW_STATUSES.includes(row.status);
        const showCollect = (row.remaining || 0) > 0 && !isTerminal;
        // B2 fix (kriter 3) — önceden bir iptal edilmiş taksitin (status:
        // "cancelled") kalıntısı (remaining>0) varsa buton GÖRSEL olarak
        // aktif kalıyordu; tıklayınca DebtLedgerSubTab.jsx::openCollection
        // guard'ı formu açmadan sessizce reddediyordu — kullanıcı için
        // sebepsiz bir "hiçbir şey olmadı". AdminAccounting.jsx'teki (satır
        // ~2146, INSTALLMENT_TERMINAL_STATUSES) AYNI desen: devre dışı
        // rozet-görünümlü <span>, aynı i18n hint anahtarı yeniden kullanılır.
        const showDisabledCollect = (row.remaining || 0) > 0 && isTerminal;
        const showInvoice = !!row.invoice_id;
        // Faz 2 (feature/moka-profil-link, 2026-09-28) — "Ödeme linki gönder"
        // aksiyonu yalnızca açık (terminal olmayan, kalanı>0) TAKSİT
        // satırlarında (row_type="invoice" satırında YOK — Faz 1'den sonra
        // kart satışı asla bare invoice üretmez, her zaman 1/1 installment
        // olur, bkz. routes/payments.py::admin_student_accounting_ledger
        // docstring'i). Kart taksidinde (method="credit_card") BİRİNCİL buton
        // (AdminAccounting.jsx'in taksit sekmesiyle AYNI stil), havale
        // taksidinde (method="bank_transfer") ikincil `ActionMenu` (kebab)
        // içinde — görev talebi.
        const showMokaAction = onMokaLink && row.row_type === 'installment' && showCollect;
        const mokaIsCard = row.method === 'credit_card';
        const mokaLinkDisabled = !mokaConfig?.link_active;
        if (!showCollect && !showDisabledCollect && !showInvoice && !showMokaAction) return <Dash />;
        return (
          <div className="flex items-center gap-2">
            {showCollect && (
              <button
                type="button"
                onClick={() => onCollect(row)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border-2 border-emerald-500 bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 border-b-4 border-b-emerald-700"
                data-testid={`collect-row-btn-${row.key}`}
              >
                <CreditCard size={13} />
                {t('accounting.actions.collect')}
              </button>
            )}
            {showDisabledCollect && (
              <span
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border-2 border-gray-200 bg-gray-50 text-[var(--text-muted)] text-xs font-bold cursor-not-allowed select-none"
                title={t('admin.acc_collect_disabled_cancelled_hint')}
                aria-label={t('admin.acc_collect_disabled_cancelled_hint')}
                data-testid={`collect-row-btn-disabled-${row.key}`}
              >
                <CreditCard size={13} />
                {t('accounting.actions.collect')}
              </span>
            )}
            {showMokaAction && mokaIsCard && (
              mokaLinkDisabled ? (
                <span
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border-2 border-gray-200 bg-gray-50 text-[var(--text-muted)] text-xs font-bold cursor-not-allowed select-none"
                  title={t('admin.moka_row_button_disabled_hint')}
                  aria-label={t('admin.moka_row_button_disabled_hint')}
                  data-testid={`moka-link-btn-disabled-${row.key}`}
                >
                  <Link2 size={13} />
                  {t('admin.moka_row_button')}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onMokaLink(row)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border-2 border-sky-500 bg-sky-500 text-white text-xs font-bold hover:bg-sky-600 border-b-4 border-b-sky-700"
                  data-testid={`moka-link-btn-${row.key}`}
                >
                  <Link2 size={13} />
                  {t('admin.moka_row_button')}
                </button>
              )
            )}
            {showInvoice && <InvoicePdfButton row={row} />}
            {showMokaAction && !mokaIsCard && (
              <ActionMenu
                items={[
                  {
                    label: t('admin.moka_row_button'),
                    icon: Link2,
                    onClick: () => onMokaLink(row),
                    disabled: mokaLinkDisabled,
                  },
                ]}
                triggerClassName="h-8 w-8"
              />
            )}
          </div>
        );
      },
    },

    // ── Varsayılan görünür (>=1280px) ────────────────────────────────────────
    {
      key: 'payment_date',
      tier: 'default',
      label: t('accounting.columns.payment_date'),
      render: (_, row) => (row.row_type === 'payment' ? fmtLedgerDate(row.date, lang) : <Dash />),
    },
    {
      key: 'payment_amount',
      tier: 'default',
      label: t('accounting.columns.payment_amount'),
      className: 'text-right',
      cellClassName: 'text-right tabular-nums font-bold',
      render: (_, row) =>
        fmtMoneyAccounting(row.row_type === 'payment' ? row.amount : 0, lang),
    },
    {
      key: 'note_number',
      tier: 'default',
      label: t('accounting.columns.promissory_note_no'),
      render: (_, row) => row.note_number || <Dash />,
    },
    {
      key: 'recorded_by',
      tier: 'default',
      label: t('accounting.columns.recorded_by'),
      render: (_, row) => row.created_by_name || <Dash />,
    },
    {
      // Muhasebe FAZ 4 (Tur 2, feature/parasut-fatura-faz4-web) — Paraşüt
      // fatura durum rozeti. Yalnızca tahsilat (row_type="payment")
      // satırlarında anlamlı; borç satırlarında hep Dash. `parasutStatusMap`
      // DebtLedgerSubTab.jsx'in TOPLU çektiği (satır başına istek YOK)
      // usePaymentsInvoiceStatusBulk sonucudur — bkz. parasutStatusCell.jsx.
      key: 'parasut_status',
      tier: 'default',
      label: t('accounting.columns.parasut_status'),
      render: (_, row) => {
        if (row.row_type !== 'payment') return <Dash />;
        return (
          <ParasutStatusCell paymentId={row.id} statusInfo={parasutStatusMap?.[row.id]} t={t} />
        );
      },
    },

    // ── "Sütun Seç" arkasında ────────────────────────────────────────────────
    {
      key: 'vat_amount',
      tier: 'optional',
      label: t('accounting.columns.vat_amount'),
      className: 'text-right',
      cellClassName: 'text-right tabular-nums',
      render: (_, row) =>
        fmtMoneyAccounting(row.row_type === 'payment' ? row.vat_amount_row : 0, lang),
    },
    {
      key: 'amount_excl_vat',
      tier: 'optional',
      label: t('accounting.columns.amount_excl_vat'),
      className: 'text-right',
      cellClassName: 'text-right tabular-nums',
      render: (_, row) =>
        fmtMoneyAccounting(row.row_type === 'payment' ? row.net_of_vat_row : 0, lang),
    },
    {
      // FAZ 1-B (Seçenek C, faiz) — yalnızca `kind: 'installment'` tahsilat
      // satırlarında dolu. Boş/0 değerde "₺0,00" gürültüsü yerine `—`
      // (`vat_amount`/`amount_excl_vat`'ın aksine bu alan çoğu satırda hiç
      // yok — faiz motoru prod'da kapalı; "veri yok" ile "sıfır faiz"
      // ayrımı burada bilinçli, o iki kolondan FARKLI davranış).
      key: 'interest_amount',
      tier: 'optional',
      label: t('accounting.columns.interest_amount'),
      className: 'text-right',
      cellClassName: 'text-right tabular-nums',
      render: (_, row) => {
        const v = row.row_type === 'payment' ? row.interest_amount_row : 0;
        return v ? fmtMoneyAccounting(v, lang) : <Dash />;
      },
    },

    // ── "Detaylar" popover (Banka + Açıklamalar) ────────────────────────────
    {
      key: 'details',
      tier: 'required',
      label: t('accounting.details_popover.label'),
      width: '48px',
      render: (_, row) => {
        const bank = row.bank_name || null;
        const notes = row.description || null;
        if (!bank && !notes) return <Dash />;
        return (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label={t('accounting.details_popover.label')}
              >
                <Info size={14} />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 text-xs space-y-2 bg-white">
              <div>
                <p className="font-bold text-[var(--text-muted)] uppercase text-[10px] tracking-wider">
                  {t('accounting.columns.bank')}
                </p>
                <p>{bank || '—'}</p>
              </div>
              <div>
                <p className="font-bold text-[var(--text-muted)] uppercase text-[10px] tracking-wider">
                  {t('accounting.columns.notes')}
                </p>
                <p className="break-words">{notes || '—'}</p>
              </div>
            </PopoverContent>
          </Popover>
        );
      },
    },
  ];
}
